import { useCallback } from 'react';
import { Box, Typography, Grid, Tooltip, IconButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Badge } from './shared';
import { NEW_TAB_LINK_PROPS } from '../../../utils/newTab';
import { formatVolume, formatDaysRemaining } from '../utils/format';
import type { EventFromAsset, EventMarketSummary } from '../api/types';
import { AppCard } from '../../../components/common/AppCard';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';

interface EventCardProps {
    event: EventFromAsset;
    isHovered?: boolean;
    onHover?: (event: EventFromAsset | null) => void;
    compact?: boolean;
    assetContext?: { ticker: string; assetType: string; name: string };
    onLoadRelated?: (event: EventFromAsset) => void;
    isSource?: boolean;
    onResetRelated?: () => void;
}

function formatShortDate(dateStr: string): string {
    const d = new Date(dateStr);
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    return `${months[d.getMonth()]} ${d.getDate()}`;
}

function MarketRow({
    market,
    barColor,
    showImage,
    isTop,
}: {
    market: EventMarketSummary;
    barColor: string;
    showImage: boolean;
    isTop?: boolean;
}) {
    const pct = (market.yes_prob * 100).toFixed(0);
    return (
        <Grid container alignItems="center" spacing={1}>
            {showImage && market.image && (
                <Grid item xs="auto">
                    <Box
                        component="img"
                        src={market.image}
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '4px',
                            objectFit: 'cover',
                        }}
                    />
                </Grid>
            )}
            <Grid item xs={3}>
                <Typography
                    sx={{
                        fontSize: '12px',
                        color: isTop ? 'text.primary' : 'text.secondary',
                        fontWeight: isTop ? 700 : 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {market.yes_outcome || market.question}
                </Typography>
            </Grid>
            <Grid item xs>
                <Box
                    sx={{
                        height: 5,
                        borderRadius: '3px',
                        overflow: 'hidden',
                        bgcolor: alpha(barColor, 0.12),
                    }}
                >
                    <Box
                        sx={{
                            width: `${market.yes_prob * 100}%`,
                            height: '100%',
                            bgcolor: isTop ? barColor : alpha(barColor, 0.5),
                            borderRadius: '3px',
                        }}
                    />
                </Box>
            </Grid>
            <Grid item xs="auto">
                <Typography
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: isTop ? barColor : alpha(barColor, 0.5),
                        minWidth: 32,
                        textAlign: 'right',
                    }}
                >
                    {pct}%
                </Typography>
            </Grid>
            <Grid item xs="auto">
                <Typography
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: 'text.secondary',
                        minWidth: 40,
                        textAlign: 'right',
                    }}
                >
                    {formatVolume(market.volume)}
                </Typography>
            </Grid>
        </Grid>
    );
}

function YesNoBar({
    market,
    yesColor,
    noColor,
}: {
    market: EventMarketSummary;
    yesColor: string;
    noColor: string;
}) {
    const yesPct = market.yes_prob * 100;
    const noPct = 100 - yesPct;
    const showYesLabel = yesPct >= 10;
    const showNoLabel = noPct >= 10;

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    height: 42,
                    borderRadius: '4px',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        width: `${yesPct}%`,
                        bgcolor: alpha(yesColor, 0.18),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: yesPct > 0 ? 24 : 0,
                    }}
                >
                    {showYesLabel && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: yesColor,
                            }}
                        >
                            Yes {yesPct.toFixed(0)}%
                        </Typography>
                    )}
                </Box>
                <Box
                    sx={{
                        width: `${noPct}%`,
                        bgcolor: alpha(noColor, 0.18),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: noPct > 0 ? 24 : 0,
                    }}
                >
                    {showNoLabel && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: noColor,
                            }}
                        >
                            No {noPct.toFixed(0)}%
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

// Tooltip content — still exported for GanttChart to use
function TooltipMarketRow({
    market,
    barColor,
}: {
    market: EventMarketSummary;
    barColor: string;
}) {
    const pct = (market.yes_prob * 100).toFixed(0);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                width: '100%',
            }}
        >
            <Typography
                sx={{
                    fontSize: '11px',
                    color: 'text.primary',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {market.yes_outcome || market.question}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width: 48,
                        height: 4,
                        borderRadius: '2px',
                        overflow: 'hidden',
                        bgcolor: alpha(barColor, 0.15),
                    }}
                >
                    <Box
                        sx={{
                            width: `${market.yes_prob * 100}%`,
                            height: '100%',
                            bgcolor: barColor,
                            borderRadius: '2px',
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: barColor,
                        minWidth: 28,
                        textAlign: 'right',
                    }}
                >
                    {pct}%
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '10px',
                        color: 'text.secondary',
                        minWidth: 36,
                        textAlign: 'right',
                    }}
                >
                    {formatVolume(market.volume)}
                </Typography>
            </Box>
        </Box>
    );
}

export function EventTooltipContent({ event }: { event: EventFromAsset }) {
    const theme = useTheme();
    const barColor = theme.palette.primary.main;
    const allMarkets = [...event.markets_summary].sort(
        (a, b) => b.yes_prob - a.yes_prob
    );
    const timeLeft = formatDaysRemaining(event.end_date);

    return (
        <Box sx={{ maxWidth: 340, p: 0.5 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                {event.image && (
                    <Box
                        component="img"
                        src={event.image}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '4px',
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            lineHeight: 1.3,
                            color: 'text.primary',
                        }}
                    >
                        {event.event_title}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 0.25,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                            }}
                        >
                            {formatShortDate(event.start_date)} -{' '}
                            {formatShortDate(event.end_date)}
                        </Typography>
                        {event.similarity != null && (
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: 'text.secondary',
                                    fontFamily: 'monospace',
                                }}
                            >
                                Relevance: {event.similarity.toFixed(2)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Stats row */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 0.75,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                    }}
                >
                    <Box
                        component="span"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                        {event.market_count}
                    </Box>{' '}
                    market{event.market_count !== 1 ? 's' : ''}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                    }}
                >
                    Vol{' '}
                    <Box
                        component="span"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                        {formatVolume(event.volume)}
                    </Box>
                </Typography>
                {timeLeft && (
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                        }}
                    >
                        {timeLeft}
                    </Typography>
                )}
                {!timeLeft && (
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'text.disabled',
                            fontFamily: 'monospace',
                        }}
                    >
                        Past est. end
                    </Typography>
                )}
            </Box>

            {/* Related tickers */}
            {event.related_tickers.length > 0 && (
                <Typography
                    sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.5 }}
                >
                    Related: {event.related_tickers.join(', ')}
                </Typography>
            )}

            {/* Asset types */}
            {event.asset_types.length > 0 && (
                <Typography
                    sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.5 }}
                >
                    Assets: {event.asset_types.join(', ')}
                </Typography>
            )}

            {/* Tags */}
            {event.tags.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'wrap',
                        mb: 0.75,
                    }}
                >
                    {event.tags.map(tag => (
                        <Badge key={tag} sx={{ fontSize: '9px' }}>
                            {tag}
                        </Badge>
                    ))}
                </Box>
            )}

            {/* Markets */}
            {allMarkets.length > 0 && (
                <>
                    <Box sx={{ height: '1px', bgcolor: 'divider', mb: 0.75 }} />
                    <Box
                        sx={{
                            maxHeight: 300,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        {allMarkets.map(m => (
                            <TooltipMarketRow
                                key={m.condition_id}
                                market={m}
                                barColor={barColor}
                            />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}

export function EventCard({
    event,
    isHovered,
    onHover,
    compact,
    assetContext,
    onLoadRelated,
    isSource,
    onResetRelated,
}: EventCardProps) {
    const theme = useTheme();
    const barColor = theme.palette.primary.main;

    const handleMouseEnter = useCallback(() => {
        onHover?.(event);
    }, [event, onHover]);

    const handleMouseLeave = useCallback(() => {
        onHover?.(null);
    }, [onHover]);

    const hasMarketImages = event.markets_summary.some(
        m => m.image && m.image !== event.image
    );
    const maxMarkets = hasMarketImages ? 2 : 5;
    const topMarkets = [...event.markets_summary]
        .sort((a, b) => b.yes_prob - a.yes_prob)
        .slice(0, maxMarkets);

    const isSingleMarket = event.markets_summary.length === 1;

    const start = new Date(event.start_date).getTime();
    const end = new Date(event.end_date).getTime();
    const now = Date.now();
    const total = end - start;
    const progress =
        total > 0 ? Math.max(0, Math.min(1, (now - start) / total)) : 0;
    const ended = now >= end;

    const eventUrl = (() => {
        const params = assetContext
            ? `?asset=${encodeURIComponent(assetContext.ticker)}&assetType=${encodeURIComponent(assetContext.assetType)}&assetName=${encodeURIComponent(assetContext.name)}`
            : '';
        return `/markets/event/${event.slug || event.event_id}${params}`;
    })();

    return (
        <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <a
                href={eventUrl}
                {...NEW_TAB_LINK_PROPS}
                style={{ textDecoration: 'none', color: 'inherit' }}
            >
                <AppCard
                    sx={{
                        overflow: 'hidden',
                        height: 275,
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.15s ease',
                        '&:hover': { boxShadow: 3 },
                        ...(isSource && {
                            border: 1,
                            borderColor: 'primary.main',
                            boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                        }),
                    }}
                >
                    {/* Padded content */}
                    <Box
                        sx={{
                            p: compact ? 1.25 : 1.5,
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Event header row */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flex: 1,
                                    mr: 1,
                                    minWidth: 0,
                                }}
                            >
                                {event.image && (
                                    <Box
                                        component="img"
                                        src={event.image}
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '6px',
                                            objectFit: 'cover',
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                    }}
                                >
                                    {event.event_title}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    {isSource && onResetRelated ? (
                                        <Tooltip title="Reset">
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onResetRelated();
                                                }}
                                                sx={{
                                                    p: 1,
                                                    m: -0.5,
                                                    flexShrink: 0,
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        color: 'error.main',
                                                    },
                                                }}
                                            >
                                                <CloseIcon
                                                    sx={{ fontSize: 16 }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    ) : onLoadRelated ? (
                                        <Tooltip title="Related events">
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onLoadRelated(event);
                                                }}
                                                sx={{
                                                    p: 1,
                                                    m: -0.5,
                                                    flexShrink: 0,
                                                    color: 'text.disabled',
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            >
                                                <AccountTreeIcon
                                                    sx={{ fontSize: 16 }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}

                                    {/*
                                    {event.slug && (
                                        <Tooltip title="Open on Polymarket">
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={`https://polymarket.com/event/${event.slug}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    p: 0.25,
                                                    flexShrink: 0,
                                                    color: 'text.disabled',
                                                    '&:hover': { color: 'primary.main' },
                                                }}
                                            >
                                                <OpenInNewIcon sx={{ fontSize: 13 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    */}
                                </Box>
                            </Box>
                        </Box>

                        {/* Markets section */}
                        {topMarkets.length > 0 && (
                            <Box
                                sx={{
                                    mb: 1,
                                    borderTop: 1,
                                    borderBottom: 1,
                                    borderColor: alpha(
                                        theme.palette.divider,
                                        0.5
                                    ),
                                    ...(isSingleMarket
                                        ? {
                                              flex: 1,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                          }
                                        : {
                                              flex: 1,
                                              minHeight: 0,
                                              overflow: 'hidden',
                                              display: 'flex',
                                              alignItems: 'center',
                                              py: 0.75,
                                          }),
                                }}
                            >
                                {isSingleMarket ? (
                                    <YesNoBar
                                        market={topMarkets[0]}
                                        yesColor={theme.palette.success.main}
                                        noColor={theme.palette.error.main}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            width: '100%',
                                        }}
                                    >
                                        {topMarkets.map((m, i) => (
                                            <MarketRow
                                                key={m.condition_id}
                                                market={m}
                                                barColor={barColor}
                                                showImage={
                                                    m.image !== event.image
                                                }
                                                isTop={i === 0}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Stats row — pushed to bottom of padded area */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1.75,
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    color: 'text.secondary',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 'inherit',
                                        fontFamily: 'inherit',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.primary',
                                        }}
                                    >
                                        {event.market_count}
                                    </Box>{' '}
                                    market{event.market_count !== 1 ? 's' : ''}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 'inherit',
                                        fontFamily: 'inherit',
                                    }}
                                >
                                    Vol{' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.primary',
                                        }}
                                    >
                                        {formatVolume(event.volume)}
                                    </Box>
                                </Typography>
                                {event.similarity != null && (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 'inherit',
                                            fontFamily: 'inherit',
                                        }}
                                    >
                                        Sim{' '}
                                        <Box
                                            component="span"
                                            sx={{
                                                color: 'warning.main',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {event.similarity.toFixed(2)}
                                        </Box>
                                    </Typography>
                                )}
                            </Box>
                            <Badge color={theme.palette.info.main}>
                                {event.market_type}
                            </Badge>
                        </Box>
                        {/* Date labels above timeline */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 1,
                                mb: 0.25,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: 'text.secondary',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {formatShortDate(event.start_date)}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: ended
                                        ? 'text.disabled'
                                        : 'text.secondary',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {formatShortDate(event.end_date)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Timeline bar — flush bottom, no padding */}
                    <Box
                        sx={{
                            height: 4,
                            bgcolor: alpha(barColor, 0.1),
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                height: '100%',
                                width: `${progress * 100}%`,
                                bgcolor: ended
                                    ? alpha(barColor, 0.35)
                                    : barColor,
                                borderRadius: '0 2px 2px 0',
                                transition: 'width 0.3s ease',
                            }}
                        />
                    </Box>
                </AppCard>
            </a>
        </Box>
    );
}
